import React, { useCallback, useState, memo } from "react";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildPromptSteps } from "@/types/flows";
import { ThumbnailContent } from "@/components/file/Carousel/ThumbnailContent";
import { useFrame } from "@/contexts/FrameContext";
import { MediaItemFileType } from "@/types/media";

export interface Thumbnail {
    thumbnailDataUrl: string;
    id: string;
    fileId?: string;
    frameId?: string | number; // uuid for annotations, segment for predictions
}

interface ThumbnailItemProps {
    thumbnail: Thumbnail;
    mode: BuildPromptSteps;
    flowLocked: boolean;
    onMouseEnter: (thumbnail: Thumbnail) => void;
    onMouseLeave: () => void;
    onThumbnailClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onEditAnnotationClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onDeleteAnnotationClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onThumbnailDragStart?: (e: React.DragEvent, thumbnail: Thumbnail) => void;
    onThumbnailDragEnd?: () => void;
}

// Memoized thumbnail item to prevent unnecessary re-renders
const ThumbnailItem = memo(function ThumbnailItem({
    thumbnail,
    mode,
    flowLocked,
    onMouseEnter,
    onMouseLeave,
    onThumbnailClick,
    onEditAnnotationClick,
    onDeleteAnnotationClick,
    onThumbnailDragStart,
    onThumbnailDragEnd
}: ThumbnailItemProps) {
    const handleMouseEnter = useCallback(() => {
        onMouseEnter(thumbnail);
    }, [onMouseEnter, thumbnail]);

    const handleDragStart = useCallback(
        (e: React.DragEvent) => {
            onThumbnailDragStart?.(e, thumbnail);
        },
        [onThumbnailDragStart, thumbnail]
    );

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            onThumbnailClick?.(e, thumbnail);
        },
        [onThumbnailClick, thumbnail]
    );

    const handleEdit = useCallback(
        (e: React.MouseEvent) => {
            onEditAnnotationClick?.(e, thumbnail);
        },
        [onEditAnnotationClick, thumbnail]
    );

    const handleDelete = useCallback(
        (e: React.MouseEvent) => {
            onDeleteAnnotationClick?.(e, thumbnail);
        },
        [onDeleteAnnotationClick, thumbnail]
    );

    return (
        <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={onMouseLeave}
            onDragStart={handleDragStart}
            onDragEnd={onThumbnailDragEnd}
            draggable={!flowLocked}
        >
            <ThumbnailContent
                thumbnail={thumbnail.thumbnailDataUrl}
                isUploading={false}
                fileType={MediaItemFileType.IMAGE}
                fileName="annotation"
                title="View annotation"
                className={twClassNames(
                    mode === BuildPromptSteps.REVIEW_PREDICTIONS && "border-dashed",
                    flowLocked ? "pointer-events-none opacity-100" : "cursor-pointer"
                )}
                onClick={handleClick}
                onDelete={!flowLocked && onDeleteAnnotationClick ? handleDelete : undefined}
                isShareMode={false}
                flowLocked={flowLocked}
                showOverlay={!flowLocked}
            />
        </div>
    );
});

interface CardThumbnailsProps {
    name: string;
    mode: BuildPromptSteps;
    thumbnails: Thumbnail[];
    selectedObject: string | null;
    previouslySelectedObject: string | null;
    flowLocked: boolean;
    onThumbnailClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onEditAnnotationClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onDeleteAnnotationClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onThumbnailDragStart?: (e: React.DragEvent, thumbnail: Thumbnail) => void;
    onThumbnailDragEnd?: () => void;
    labelColor?: string;
}

export const CardThumbnails: React.FC<CardThumbnailsProps> = ({
    name,
    mode,
    thumbnails,
    selectedObject,
    previouslySelectedObject,
    flowLocked,
    onThumbnailClick,
    onEditAnnotationClick,
    onDeleteAnnotationClick,
    onThumbnailDragStart,
    onThumbnailDragEnd,
    labelColor
}) => {
    const [hoveredThumbnailId, setHoveredThumbnailId] = useState<string | null>(null);
    const { setHoveredAnnotationId } = useFrame();

    const handleThumbnailMouseEnter = useCallback(
        (thumbnail: Thumbnail) => {
            setHoveredThumbnailId(thumbnail.id);
            // Always set the hovered annotation ID to enable canvas highlighting
            // The canvas will only show/highlight it if it's on the current frame
            setHoveredAnnotationId(thumbnail.id);
        },
        [setHoveredAnnotationId]
    );

    const handleThumbnailMouseLeave = useCallback(() => {
        setHoveredThumbnailId(null);
        setHoveredAnnotationId(null);
    }, [setHoveredAnnotationId]);

    return (
        <div className="flex w-full gap-2 overflow-x-auto px-4 py-1">
            {thumbnails.length > 0 ? (
                <>
                    {thumbnails.map(
                        (thumbnail, index) =>
                            thumbnail &&
                            thumbnail.thumbnailDataUrl && (
                                <ThumbnailItem
                                    key={`${name}-thumb-${thumbnail.id}-${thumbnail.fileId}`}
                                    thumbnail={thumbnail}
                                    mode={mode}
                                    flowLocked={flowLocked}
                                    onMouseEnter={handleThumbnailMouseEnter}
                                    onMouseLeave={handleThumbnailMouseLeave}
                                    onThumbnailClick={onThumbnailClick}
                                    onEditAnnotationClick={onEditAnnotationClick}
                                    onDeleteAnnotationClick={onDeleteAnnotationClick}
                                    onThumbnailDragStart={onThumbnailDragStart}
                                    onThumbnailDragEnd={onThumbnailDragEnd}
                                />
                            )
                    )}
                </>
            ) : (
                !(flowLocked && mode !== BuildPromptSteps.REVIEW_PREDICTIONS) && (
                    <div
                        className={twClassNames(
                            "fileThumbnail border border-dashed border-gray-300"
                        )}
                    >
                        {mode === BuildPromptSteps.REVIEW_PREDICTIONS && (
                            <span className="text-center text-xs">
                                Adjust <br /> slider
                            </span>
                        )}
                    </div>
                )
            )}
        </div>
    );
};
